import messages from './messages';

// Cloudinary's default chunk size; min 5 MB, required for files >100 MB.
const CHUNK = 20 * 1024 * 1024;

// Per-chunk upload ceiling. A socket that connects then goes silent never fires load/error, so
// without this the promise never settles and the composer stays locked with no way to cancel.
const UPLOAD_TIMEOUT_MS = 120 * 1000;

// Asks our backend to sign a scoped video upload (folder, authenticated delivery, allowed formats).
// qapi.messages routes it to the V2 API with auth — same-origin in V2, cross-origin to V2 in V1
// (via V2_URL + the JWT bridge). `sign` is swappable so pitch videos can use their own signature
// endpoint (a different Cloudinary folder) while sharing the whole upload pipeline.
const signVideoUpload = async (sign = messages.signVideoUpload) => {
  const { data, error } = await sign();
  if (error || !data) {
    throw new Error('Could not authorize the video upload');
  }
  return data;
};

/**
 * Upload a video blob/file straight to Cloudinary (signed, chunked) — the bytes never pass through
 * Rails. Returns the reference our backend persists (see MessageSender#create_video_attachment).
 * @param {Blob|File} blob - the recorded or picked video
 * @param {object} [opts]
 * @param {(fraction:number)=>void} [opts.onProgress] - 0..1 upload progress
 * @returns {Promise<{public_id, resource_type, format, duration, bytes}>}
 */
export const uploadVideoToCloudinary = async (blob, { onProgress, sign } = {}) => {
  const sig = await signVideoUpload(sign);

  const signedParams = {
    api_key: sig.api_key,
    timestamp: sig.timestamp,
    signature: sig.signature,
    folder: sig.folder,
    type: sig.type,
    allowed_formats: sig.allowed_formats,
    // Pre-generates the playback MP4 derivative (see V2's VideoSignaturesController::EAGER) — part
    // of the signature, so must be sent exactly as signed.
    eager: sig.eager,
    eager_async: sig.eager_async,
  };
  const buildForm = (extra) => {
    const fd = new FormData();
    Object.entries({ ...signedParams, ...extra }).forEach(([key, value]) => fd.append(key, value));
    return fd;
  };

  // fetch can't report UPLOAD progress, so the bar froze at 0% for every single-shot upload
  // (all recorded clips are well under CHUNK). XHR exposes upload.onprogress.
  const postForm = (url, form, { headers = {}, onUploadProgress } = {}) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    Object.entries(headers).forEach(([key, value]) => xhr.setRequestHeader(key, value));
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onUploadProgress?.(event.loaded / event.total);
      }
    };
    xhr.onload = () => {
      let body;
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        reject(new Error('Video upload failed'));
        return;
      }
      // A chunk can complete the request yet carry an HTTP error (Cloudinary returns a JSON error
      // body). Reject here so `await postForm` throws and the loop aborts immediately, instead of
      // POSTing the remaining chunks pointlessly and only failing after the whole file uploaded.
      if (xhr.status >= 400 || body?.error) {
        reject(new Error(body?.error?.message || `Video upload failed (${xhr.status})`));
        return;
      }
      resolve(body);
    };
    xhr.onerror = () => reject(new Error('Video upload failed'));
    xhr.timeout = UPLOAD_TIMEOUT_MS;
    xhr.ontimeout = () => reject(new Error('Video upload timed out'));
    xhr.send(form);
  });

  let info;
  if (blob.size <= CHUNK) {
    info = await postForm(sig.upload_url, buildForm({ file: blob }), {
      onUploadProgress: (fraction) => onProgress?.(fraction),
    });
    onProgress?.(1);
  } else {
    // One signature covers every chunk; X-Unique-Upload-Id ties them together for Cloudinary.
    const uniqueId = `${Date.now()}-${Math.round(Math.random() * 1e6)}`;
    for (let start = 0; start < blob.size; start += CHUNK) {
      const end = Math.min(start + CHUNK, blob.size);
      // eslint-disable-next-line no-await-in-loop -- chunks must upload sequentially
      info = await postForm(sig.upload_url, buildForm({ file: blob.slice(start, end) }), {
        headers: { 'X-Unique-Upload-Id': uniqueId, 'Content-Range': `bytes ${start}-${end - 1}/${blob.size}` },
        onUploadProgress: (fraction) => onProgress?.(Math.min((start + fraction * (end - start)) / blob.size, 1)),
      });
      onProgress?.(Math.min(end / blob.size, 1));
    }
  }

  if (!info || info.error) {
    throw new Error(info?.error?.message || 'Video upload failed');
  }

  return {
    public_id: info.public_id,
    resource_type: info.resource_type,
    format: info.format,
    duration: info.duration,
    bytes: info.bytes,
  };
};

export default { uploadVideoToCloudinary };
